import { Component, computed, input } from '@angular/core';
import { NotificationLayoutComponent } from '../../notification-detail-layout/notification-detail-layout.component';
import { environment } from '../../../../environments/environment';
import { NotificationStep, TeacherNotification } from '../../../core/models/notification.model';

@Component({
  selector: 'wf-struggling-task-notification',
  imports: [
    NotificationLayoutComponent
  ],
  template: `
    <wf-notification-layout
      headerTitle="💬 A student could use your support"
      [preambles]="[{
        text: 'The student(s) below had a tough time with a task in WordFlight. '
          + 'The good news: WordFlight will keep adapting to their level as they move forward. '
          + 'A quick check-in from you can make a real difference.'
      }]"
      [steps]="steps"
      [ids]="ids()"
      ctaLabel="Open Unit Detail report"
      ctaRoute="/reports/system/unit-detail"
      closingText="We know they can do it — and so do you. Reach out to your Partner Success Lead anytime if you’d like extra guidance."
    >
      <table table class="table-wrap">
        <tr>
          <th class="th">Student</th>
          <th class="th">Date</th>
          <th class="th">Unit</th>
          <th class="th">Task</th>
          <th class="th">Word List</th>
          <th class="th">Score</th>
        </tr>
        @for (notification of data(); track notification.alertID) {
          <tr>
            <td class="td">{{ notification.studentName }}</td>
            <td class="td" style="white-space:nowrap; color:#a2a2a2;">{{ notification.data['displaydate'] }}</td>
            <td class="td">{{ notification.data.unit }}</td>
            <td class="td">{{ notification.data.taskname }}</td>
            <td class="td">{{ notification.data.wordlist }}</td>
            <td class="td"><span [class]="getScoreClass(notification.data.score)">{{ notification.data.score }}</span></td>
          </tr>
        }
      </table>
    </wf-notification-layout>
  `,
  standalone: true,
})
export class StrugglingTaskNotificationComponent {
  private readonly studentAppLink = environment.studentSiteURL;
  readonly data = input.required<TeacherNotification[] | null>();
  readonly ids = computed(() => {
    const notifications = this.data() ?? [];

    return { alerts: notifications.map(notification => notification.alertID) };
  });
  readonly steps: NotificationStep[] = [
    {
      title: 'Check in with the student',
      body: 'Ask them how the task went and if they have any questions. Your encouragement matters more than you know.'
    },
    {
      title: 'Review their responses in the Unit Detail report',
      body: 'Open the Unit Detail report, select the student, then click the + next to the task to see exactly how they responded to each trial.'
    },
    {
      title: 'Try the task together',
      body: 'Log in to the <a href="' + this.studentAppLink + '" target="_blank">student demo program</a> and work through the task side-by-side. '
        + 'Use the Unit Detail report to find the Unit, Level, and Word List. Check the Group Summary report for their Instructional Zone if needed.'
    },
    {
      title: 'Access small group resources if needed',
      body: 'If the student needs one-on-one concept support, visit '
        + '<a href="#" data-route="/small-group/system/all/model-lessons">Small Group Instruction</a> in the Management Site '
        + 'for lesson plans and resources tailored to the skill they’re working on.',
      variant: 'green',
    }
  ];

  getScoreClass(scoreStr: string): string {
    const score = parseFloat(scoreStr);

    if (isNaN(score)) return 'badge-default';
    if (score < 40) return 'badge-alert';
    if (score < 70) return 'badge-warn';

    return 'badge-green';
  }
}
